import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PhotoLightbox from '../components/PhotoLightbox';
import { supabase } from '../lib/supabase';
import { EyeSlashIcon, TrophyIcon } from '@heroicons/react/24/outline';
import type { ContentItem, Champion, ChampionsData, PressArticle, PhotoCategory, Photo } from '../types/content';

interface TournamentChampion {
  id: string;
  tournament_id: string;
  division: 'male' | 'female';
  player_name: string;
  player_country: string;
  score: string;
  player_image: string | null;
  highlights: string[];
}

interface TournamentWithChampions {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  year: number;
  hasLeaderboard: boolean;
  champions: {
    male?: TournamentChampion;
    female?: TournamentChampion;
  };
}

interface GalleryItem {
  id: string;
  tournament_id: string | null;
  categories: PhotoCategory[];
}

interface SimpleTournament {
  id: string;
  name: string;
  start_date: string;
}

function History() {
  const navigate = useNavigate();
  const [championPhotoUrl, setChampionPhotoUrl] = useState<string | null>(null);
  const [isChampionLightboxOpen, setIsChampionLightboxOpen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [selectedPhotos, setSelectedPhotos] = useState<Photo[]>([]);
  const [isPublished, setIsPublished] = useState(true);
  const [tournaments, setTournaments] = useState<TournamentWithChampions[]>([]);
  const [allTournaments, setAllTournaments] = useState<SimpleTournament[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>('');
  const [hasUserSelected, setHasUserSelected] = useState(false);
  const [selectedTournamentForGallery, setSelectedTournamentForGallery] = useState<string>('');
  const [allGalleryItems, setAllGalleryItems] = useState<GalleryItem[]>([]);
  const [tournamentsWithContent, setTournamentsWithContent] = useState<Set<string>>(new Set());
  const [pressArticles, setPressArticles] = useState<{
    id: string;
    tournament_id: string | null;
    source: string;
    title: string;
    date: string | null;
    quote: string;
    link: string;
    image_url: string | null;
  }[]>([]);
  const [pageContent, setPageContent] = useState<{
    title: string;
    description: string;
    categories: PhotoCategory[];
    pressHighlights: PressArticle[];
  }>({
    title: 'Tournament History',
    description: 'From the Inaugural 2024 Champions to Today.',
    categories: [],
    pressHighlights: []
  });

  useEffect(() => {
    fetchContent();
    fetchTournamentChampions();
    fetchAllTournaments();
    checkHistoryPublishStatus();
    fetchPressArticles();
  }, []);

  useEffect(() => {
    const ids = new Set<string>();
    tournaments.forEach(t => ids.add(t.id));
    allGalleryItems.forEach(g => { if (g.tournament_id) ids.add(g.tournament_id); });
    setTournamentsWithContent(ids);

    if (!hasUserSelected) {
      if (tournaments.length > 0) {
        setSelectedTournamentId(tournaments[0].id);
      } else if (allTournaments.length > 0) {
        const mostRecent = allTournaments.find(t => ids.has(t.id));
        const fallback = mostRecent ? mostRecent.id : (allTournaments[0]?.id ?? '');
        if (fallback) setSelectedTournamentId(fallback);
      }
    }
  }, [tournaments, allGalleryItems, allTournaments]);

  useEffect(() => {
    setSelectedTournamentForGallery(selectedTournamentId);
  }, [selectedTournamentId]);

  useEffect(() => {
    fetchContent();
  }, [selectedTournamentForGallery]);

  useEffect(() => {
    fetchPressArticles();
  }, [selectedTournamentId]);

  const checkHistoryPublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'history_published')
        .maybeSingle();

      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking history publish status:', error);
        return;
      }

      // If the setting exists, use its value, otherwise default to true
      setIsPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking history publish status:', err);
    }
  };

  const fetchAllTournaments = async () => {
    try {
      const { data, error } = await supabase
        .from('tournaments')
        .select('id, name, start_date')
        .order('start_date', { ascending: false });

      if (error) throw error;
      if (data) {
        setAllTournaments(data);
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    }
  };

  const fetchTournamentChampions = async () => {
    try {
      // Fetch completed tournaments with their champions
      const { data: tournamentsData, error: tournamentsError } = await supabase
        .from('tournaments')
        .select('*')
        .eq('status', 'completed')
        .order('start_date', { ascending: false });

      if (tournamentsError) throw tournamentsError;

      if (!tournamentsData || tournamentsData.length === 0) {
        return;
      }

      const tournamentIds = tournamentsData.map(t => t.id);

      // Fetch champions and leaderboard counts in parallel
      const [championsResult, leaderboardResult] = await Promise.all([
        supabase.from('tournament_champions').select('*').in('tournament_id', tournamentIds),
        supabase.from('leaderboard_view').select('tournament_id').in('tournament_id', tournamentIds)
      ]);

      if (championsResult.error) throw championsResult.error;

      const championsData = championsResult.data;
      const leaderboardTournamentIds = new Set((leaderboardResult.data || []).map(r => r.tournament_id));

      // Combine tournaments with their champions
      const tournamentsWithChampions: TournamentWithChampions[] = tournamentsData.map(tournament => {
        const tournamentChampions = championsData?.filter(c => c.tournament_id === tournament.id) || [];
        const maleChampion = tournamentChampions.find(c => c.division === 'male');
        const femaleChampion = tournamentChampions.find(c => c.division === 'female');

        return {
          id: tournament.id,
          name: tournament.name,
          start_date: tournament.start_date,
          end_date: tournament.end_date,
          year: new Date(tournament.start_date).getFullYear(),
          hasLeaderboard: leaderboardTournamentIds.has(tournament.id),
          champions: {
            male: maleChampion,
            female: femaleChampion
          }
        };
      });

      // Only show tournaments that have at least one champion
      const tournamentsWithAtLeastOneChampion = tournamentsWithChampions.filter(
        t => t.champions.male || t.champions.female
      );

      setTournaments(tournamentsWithAtLeastOneChampion);
    } catch (error) {
      console.error('Error fetching tournament champions:', error);
    }
  };

  const fetchPressArticles = async () => {
    try {
      let query = supabase
        .from('press_articles')
        .select('*')
        .order('date', { ascending: false, nullsFirst: false });

      if (selectedTournamentId) {
        query = query.eq('tournament_id', selectedTournamentId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setPressArticles(data || []);
    } catch (err) {
      console.error('Error fetching press articles:', err);
    }
  };

  const fetchContent = async () => {
    try {
      const { data: sections, error } = await supabase
        .from('content_sections')
        .select(`
          id,
          title,
          content_items (*)
        `)
        .eq('page', 'history');

      if (error) throw error;

      if (sections) {
        const headerSection = sections.find(s => s.title === 'Page Header');
        const gallerySection = sections.find(s => s.title === 'Gallery');
        const pressSection = sections.find(s => s.title === 'Press');

        const getContentItem = (section: any, title: string): ContentItem | undefined => {
          return section?.content_items?.find((item: ContentItem) =>
            item.title === title && item.status === 'Published'
          );
        };

        const parseContent = (item: ContentItem | undefined) => {
          if (!item?.content) return null;
          try {
            return typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
          } catch {
            return item.content;
          }
        };

        // Parse header content
        const titleContent = getContentItem(headerSection, 'Title');
        const descriptionContent = getContentItem(headerSection, 'Description');

        // Get all gallery items with tournament associations
        const galleryItems = gallerySection?.content_items?.filter(
          (item: any) => item.status === 'Published' && item.type === 'gallery'
        ) || [];

        // Parse all gallery items and store with tournament associations
        const parsedGalleryItems: GalleryItem[] = [];
        galleryItems.forEach((item: any) => {
          try {
            let parsedContent = item.content;

            // Handle multiple levels of JSON encoding
            let parseAttempts = 0;
            while (typeof parsedContent === 'string' && parseAttempts < 5) {
              try {
                parsedContent = JSON.parse(parsedContent);
                parseAttempts++;
              } catch (err) {
                console.error('Error parsing gallery content:', err);
                break;
              }
            }

            if (Array.isArray(parsedContent)) {
              parsedGalleryItems.push({
                id: item.id,
                tournament_id: item.tournament_id,
                categories: parsedContent
              });
            }
          } catch (e) {
            console.error('Error parsing gallery content:', e, item);
          }
        });

        setAllGalleryItems(parsedGalleryItems);

        // Get categories to display based on selected tournament filter
        const filteredGalleryItems = selectedTournamentForGallery
          ? parsedGalleryItems.filter(item => item.tournament_id === selectedTournamentForGallery)
          : parsedGalleryItems;

        const allGalleryCategories: PhotoCategory[] = [];
        filteredGalleryItems.forEach(item => {
          allGalleryCategories.push(...item.categories);
        });

        // Parse press content
        const pressContent = getContentItem(pressSection, 'Press Articles');
        let parsedPressContent = [];

        if (pressContent) {
          try {
            parsedPressContent = typeof pressContent.content === 'string'
              ? JSON.parse(pressContent.content)
              : pressContent.content;

            if (!Array.isArray(parsedPressContent)) {
              parsedPressContent = [];
            }
          } catch (e) {
            console.error('Error parsing press content:', e);
            parsedPressContent = [];
          }
        }

        setPageContent({
          title: parseContent(titleContent) || pageContent.title,
          description: parseContent(descriptionContent) || pageContent.description,
          categories: allGalleryCategories,
          pressHighlights: parsedPressContent || []
        });
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const openGallery = (categoryIndex: number, photoIndex: number) => {
    const category = pageContent.categories[categoryIndex];

    if (!category || !category.photos || category.photos.length === 0) {
      console.warn('No photos available in this category');
      return;
    }

    // Filter to get only valid photos
    const validPhotos = category.photos.filter(photo => photo && photo.url && photo.url.trim() !== '');

    if (validPhotos.length === 0) {
      console.warn('No valid photos found in this category');
      return;
    }

    // Ensure the photo index is within bounds
    const safePhotoIndex = Math.max(0, Math.min(photoIndex, validPhotos.length - 1));

    setSelectedCategory(categoryIndex);
    setSelectedPhotoIndex(safePhotoIndex);
    setSelectedPhotos(validPhotos);
    setIsGalleryOpen(true);
  };

  return (
    <div className="pt-20">
      {championPhotoUrl && (
        <PhotoLightbox
          isOpen={isChampionLightboxOpen}
          onClose={() => { setIsChampionLightboxOpen(false); setChampionPhotoUrl(null); }}
          photos={[{ url: championPhotoUrl, caption: 'Tournament Champion' }]}
          initialIndex={0}
        />
      )}

      {/* Unpublished Notice */}
      {!isPublished && (
        <div className="fixed top-24 inset-x-0 z-50 bg-yellow-100 border-b border-yellow-200 py-2">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-2 text-yellow-800">
              <EyeSlashIcon className="w-5 h-5" />
              <p className="text-sm font-medium">
                This page is currently unpublished and only visible to administrators
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-golf-gray text-white py-32">
        <div className="absolute inset-0">
          <video
            className="w-full h-full object-cover opacity-30"
            autoPlay
            loop
            muted
            playsInline
            poster="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa"
          >
            <source
              src="https://player.vimeo.com/external/454507822.sd.mp4?s=c602c13a7c6cd3535a37d4b9176a3ca4a5951c8c&profile_id=164&oauth2_token_id=57447761"
              type="video/mp4"
            />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/50" />
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-6xl font-display font-bold mb-6">
              {pageContent.title}
            </h1>
            <p className="text-xl text-golf-sand">
              {pageContent.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tournament Selector */}
      {tournamentsWithContent.size > 0 && (
        <section className="bg-white border-b border-gray-200 sticky top-[72px] z-30 shadow-sm">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-2 overflow-x-auto py-3 scrollbar-hide">
              {allTournaments.filter(t => tournamentsWithContent.has(t.id)).map(t => (
                <button
                  key={t.id}
                  onClick={() => { setHasUserSelected(true); setSelectedTournamentId(t.id); }}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedTournamentId === t.id
                      ? 'bg-golf-orange text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tournament Champions Section */}
      {tournaments.length > 0 ? (
        tournaments
          .filter(t => t.id === selectedTournamentId)
          .map((tournament, tournamentIndex) => (
          <section key={tournament.id} className={tournamentIndex % 2 === 0 ? 'py-24 bg-white' : 'py-24 bg-gray-50'}>
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="max-w-7xl mx-auto"
              >
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-display font-bold text-golf-gray mb-4">
                    {tournament.name} Champions
                  </h2>
                  <p className={`text-xl text-gray-600 ${tournament.hasLeaderboard ? 'mb-6' : ''}`}>
                    Tournament Winners
                  </p>
                  {tournament.hasLeaderboard && (
                    <button
                      onClick={() => navigate(`/leaderboard?tournament=${tournament.id}`)}
                      className="inline-flex items-center gap-2 bg-golf-orange text-white px-6 py-3 rounded-full font-semibold hover:bg-orange-600 transition-colors shadow-md"
                    >
                      <TrophyIcon className="w-5 h-5" />
                      View Full Leaderboard
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-12">
                  {tournament.champions.male && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="bg-white rounded-3xl shadow-xl overflow-hidden group"
                    >
                      <div className="relative h-96">
                        <img
                          src={tournament.champions.male.player_image || 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800'}
                          alt={tournament.champions.male.player_name}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => { setChampionPhotoUrl(tournament.champions.male!.player_image || 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800'); setIsChampionLightboxOpen(true); }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none" />
                        <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-sm rounded-lg px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          <p className="text-white text-xs font-medium">View Full Photo</p>
                        </div>
                        <div className="absolute bottom-0 left-0 p-8 w-full">
                          <p className="text-golf-orange font-medium mb-2 uppercase tracking-wider">
                            Male Division Champion
                          </p>
                          <h3 className="text-4xl font-display font-bold text-white mb-2">
                            {tournament.champions.male.player_name}
                          </h3>
                          <div className="flex items-center gap-4 text-white/80">
                            <span>{tournament.champions.male.player_country}</span>
                            <span className="w-1.5 h-1.5 rounded-full bg-golf-orange" />
                            <span className="text-golf-orange font-bold">{tournament.champions.male.score}</span>
                          </div>
                        </div>
                      </div>
                      {tournament.champions.male.highlights && tournament.champions.male.highlights.length > 0 && (
                        <div className="p-8">
                          <ul className="space-y-4">
                            {tournament.champions.male.highlights.map((highlight, i) => (
                              <li key={i} className="flex items-start text-gray-600">
                                <div className="w-2 h-2 bg-golf-orange rounded-full mt-2 mr-3 flex-shrink-0" />
                                <span>{highlight}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {tournament.champions.female && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                      className="bg-white rounded-3xl shadow-xl overflow-hidden group"
                    >
                      <div className="relative h-96">
                        <img
                          src={tournament.champions.female.player_image || 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=800'}
                          alt={tournament.champions.female.player_name}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => { setChampionPhotoUrl(tournament.champions.female!.player_image || 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=800'); setIsChampionLightboxOpen(true); }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none" />
                        <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-sm rounded-lg px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          <p className="text-white text-xs font-medium">View Full Photo</p>
                        </div>
                        <div className="absolute bottom-0 left-0 p-8 w-full">
                          <p className="text-golf-orange font-medium mb-2 uppercase tracking-wider">
                            Female Division Champion
                          </p>
                          <h3 className="text-4xl font-display font-bold text-white mb-2">
                            {tournament.champions.female.player_name}
                          </h3>
                          <div className="flex items-center gap-4 text-white/80">
                            <span>{tournament.champions.female.player_country}</span>
                            <span className="w-1.5 h-1.5 rounded-full bg-golf-orange" />
                            <span className="text-golf-orange font-bold">{tournament.champions.female.score}</span>
                          </div>
                        </div>
                      </div>
                      {tournament.champions.female.highlights && tournament.champions.female.highlights.length > 0 && (
                        <div className="p-8">
                          <ul className="space-y-4">
                            {tournament.champions.female.highlights.map((highlight, i) => (
                              <li key={i} className="flex items-start text-gray-600">
                                <div className="w-2 h-2 bg-golf-orange rounded-full mt-2 mr-3 flex-shrink-0" />
                                <span>{highlight}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </div>
          </section>
        ))
      ) : null}

      {tournaments.length > 0 && selectedTournamentId &&
        !tournaments.find(t => t.id === selectedTournamentId) && (
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto text-center">
              <TrophyIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No champion data available for this tournament yet.</p>
            </div>
          </div>
        </section>
      )}

      {tournaments.length === 0 && (
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto text-center">
              <p className="text-gray-500">No tournament champions to display yet.</p>
            </div>
          </div>
        </section>
      )}

      {/* Photo Gallery Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-7xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-display font-bold text-golf-gray mb-2">
                Tournament Gallery
              </h2>
              <p className="text-xl text-gray-600">
                {allTournaments.find(t => t.id === selectedTournamentId)
                  ? `Photos from ${allTournaments.find(t => t.id === selectedTournamentId)?.name}`
                  : 'Capturing the moments that made history'}
              </p>
            </div>

            {pageContent.categories.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No gallery categories available yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-8">
                {pageContent.categories.map((category, categoryIndex) => {
                  const validPhotos = category.photos?.filter(photo => photo && photo.url && photo.url.trim() !== '') || [];
                  const photoCount = validPhotos.length;
                  const displayThumbnails = validPhotos.slice(0, 3);

                  return (
                    <motion.div
                      key={categoryIndex}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
                      className="group"
                    >
                      {/* Main Category Image */}
                      <div
                        className="relative aspect-[4/3] rounded-xl overflow-hidden mb-4 cursor-pointer"
                        onClick={() => openGallery(categoryIndex, 0)}
                      >
                        {validPhotos.length > 0 && validPhotos[0]?.url ? (
                          <>
                            <img
                              src={validPhotos[0].url}
                              alt={category.name}
                              className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent">
                              <div className="absolute bottom-0 left-0 p-6 w-full">
                                <h3 className="text-2xl font-display font-bold text-white mb-2">
                                  {category.name}
                                </h3>
                                <p className="text-golf-sand">
                                  {photoCount} {photoCount === 1 ? 'Photo' : 'Photos'}
                                </p>
                              </div>
                            </div>
                            <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-white text-sm font-medium">View Gallery</p>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <div className="text-center">
                              <p className="text-gray-500 mb-2">{category.name}</p>
                              <p className="text-gray-400 text-sm">No photos available</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Thumbnail Grid */}
                      {displayThumbnails.length > 0 && (
                        <div className="grid grid-cols-3 gap-2">
                          {displayThumbnails.map((photo, thumbIndex) => (
                            <div
                              key={thumbIndex}
                              className="relative aspect-square rounded-lg overflow-hidden cursor-pointer transform hover:scale-105 transition-transform duration-300"
                              onClick={(e) => {
                                e.stopPropagation();
                                openGallery(categoryIndex, thumbIndex);
                              }}
                            >
                              <img
                                src={photo.url}
                                alt={photo.caption || `${category.name} photo ${thumbIndex + 1}`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors duration-300"></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Gallery Lightbox */}
            {isGalleryOpen && selectedPhotos.length > 0 && (
              <PhotoLightbox
                isOpen={isGalleryOpen}
                onClose={() => {
                  setIsGalleryOpen(false);
                  setSelectedPhotos([]);
                  setSelectedPhotoIndex(0);
                }}
                photos={selectedPhotos}
                initialIndex={selectedPhotoIndex}
              />
            )}
          </motion.div>
        </div>
      </section>

      {/* Press Coverage Section */}
      {pressArticles.length > 0 && (
        <section className="py-24 bg-golf-sand">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-7xl mx-auto"
            >
              <div className="text-center mb-16">
                <h2 className="text-4xl font-display font-bold text-golf-gray mb-4">
                  Press Coverage
                </h2>
                <p className="text-xl text-gray-600">
                  {allTournaments.find(t => t.id === selectedTournamentId)
                    ? `Media coverage of ${allTournaments.find(t => t.id === selectedTournamentId)?.name}`
                    : 'What the media is saying about Ibom Open'}
                </p>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {pressArticles.map((article, index) => (
                  <motion.article
                    key={article.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-golf-orange font-medium">{article.source}</span>
                      <span className="text-gray-500 text-sm">
                        {article.date
                          ? new Date(article.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                          : ''}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-golf-gray mb-4 line-clamp-2 hover:text-golf-orange transition-colors duration-300">
                      {article.title}
                    </h3>

                    <div className="relative mb-6">
                      <div className="absolute left-0 top-0 w-1 h-full bg-golf-orange rounded-full" />
                      <blockquote className="pl-6 text-gray-600 italic">
                        "{article.quote}"
                      </blockquote>
                    </div>

                    <div className="mt-auto">
                      <a
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-golf-orange hover:text-golf-green transition-colors font-medium group"
                      >
                        Read Full Article
                        <span className="ml-2 transform group-hover:translate-x-1 transition-transform">→</span>
                      </a>
                    </div>
                  </motion.article>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
}

export default History;